import { zonedTimeToUtc } from 'date-fns-tz'
import { camelCase, mapKeys, snakeCase } from 'lodash-es'

import { mapApiToAppointment, type RawAppointment } from 'core/appointments/infra/appointmentMapper'
import { type RawContainer } from 'core/container-details/domain/ShipmentContainer'
import { mapApiToContainer } from 'core/container-details/infra/ContainerMapper'
import { type EquipmentSubtypes } from 'core/domain/Load'
import { type RawManagedBy } from 'core/domain/ManagedBy'
import { type Mode } from 'core/domain/Mode'
import { type StopType } from 'core/domain/Stop'
import { type TruckType } from 'core/domain/Truck'
import { type RawLoadMeasures } from 'core/infra/LoadMeasuresMapper'
import { mapApiToManagedBy } from 'core/infra/ManagedByMapper.ts'
import { type RawItem } from 'core/shipping-items/domain/RawShippingItems'
import {
  mapApiToShippingItems,
  mapApiToShippingMeasures,
} from 'core/shipping-items/infra/ShippingItemsMapper'
import { formatDate, formatISO, parseISO } from 'core/utils/date'
import { numberFormatter } from 'core/utils/number'
import { isEmpty } from 'core/utils/string'

import {
  mapApiToEquipmentRequirements,
  type RawEquipmentRequirements,
} from 'tender/infra/TenderMapper'

import { type DocumentStatus, type POD } from '../domain/Document'
import { type Employee } from '../domain/Employee'
import { type Contact, type FacilityType } from '../domain/Facility'
import { type Invoice } from '../domain/Invoice'
import type {
  Carrier,
  EffectiveCarrier,
  OpenDockWarehouseDetails,
  Shipment,
  ShipperBusiness,
  Stop,
} from '../domain/Shipment'
import { type ScheduleFormValues } from '../ui/OpenDockAppointmentDrawer/Schedule'
import { type RawEmployee } from './CarrierMapper'

export type RawShipmentMeasures = {
  total_weight: number | string
  total_volume: number | string
  density?: number | string
  total_linear_feet: number | string
}

type RawStop = {
  id: string | number
  stop_type: StopType
  stop_index: number
  date: string
  facility?: RawFacility
  city: string
  state: string
  zipcode: string
  country: string
  address: string | null
  default_contact_uuid: string | null
  notes?: string
  blind_bol?: boolean
  scale_ticket?: boolean
  lat?: string
  lng?: string
  contacts?: Array<RawContact>
  contact?: RawContact | null
  check_in?: {
    done_at?: string
  }
  check_out?: {
    done_at?: string
  }
  check_in_at: string | null
  check_out_at: string | null
  checkin_timeliness: string
  checkout_timeliness: string
  is_checkin_late: boolean
  is_checkout_late: boolean
  timezone: string
  appointment: RawAppointment | null
}

type RawFacility = {
  uuid: string
  city: string
  state: string
  country: string
  company_name?: string
  zipcode: string
  address: string
  contacts: Array<RawContact>
  default_contact_uuid?: string
  hours_of_operations?: RawFacilityOperation
  warehouse_uuid?: string
  warehouse_id?: string
  warehouse_name?: string
  timezone?: string
  type: FacilityType
}

type RawFacilityOperation = {
  mode: string
  opens: string
  closes: string
  monday: boolean
  tuesday: boolean
  wednesday: boolean
  thursday: boolean
  friday: boolean
  saturday: boolean
  sunday: boolean
}

type RawCarrier = {
  company_name: string | null
  dot: string | null
  entity: 'broker' | 'asset' | null
  id: number
  mc: string | null
  name: string
  uuid: string
  scac: string | null
  email: string
}

type RawContact = {
  uuid: string
  email?: string | null
  name?: string
  phone_number_extension?: string | null
  phone_number: string
}

type RawEffectiveCarrier = {
  name: string
  dot: string | null
  mc: string | null
}

export type RawAttachment = {
  id: number
  file_name: string
}

type RawAccessorial = {
  code: string
  name: string
  stop_type: Array<'pickup' | 'delivery'>
}

type RawPaymentTerm = 'prepaid' | 'collect' | 'third_party'

export type RawShipment = {
  uuid: string
  shipper_guide_id: string
  blind_bol?: boolean
  accessorials: Array<RawAccessorial>
  spot_quote?: {
    uuid: string
    shipper_guide_id: string
    status: string
    booked_rate: {
      uuid: string
      carrier: string | null
      flat_rate: string | null
      ltl_carrier?: string
      per_mile_rate?: string
    } | null
    tendered_rate: null
    currency: string
    conversion_rate: number | null
    attachments: Array<RawAttachment>
    notes: string | null
  } | null
  mode: Mode
  equipment_type: TruckType
  items: Array<RawItem>
  managed_by: RawManagedBy
  mileage: number
  shipper_ref_number: string
  hazmat: boolean
  bol_number?: string
  po_numbers?: string[]
  so_numbers?: string[]
  stops: Array<RawStop>
  intermediaryStops?: Array<RawStop>
  temperature?: number
  carrier: RawCarrier
  effective_carrier: RawEffectiveCarrier | null
  shipper: {
    company_name: string
    logo_email_url: string
    email?: string
    business?: ShipperBusiness
  }
  measures: RawLoadMeasures
  flat_rate?: string
  is_loadsmart_carrier: boolean
  ref_number?: string | number
  status: string
  equipment_length: string
  equipment_requirements?: RawEquipmentRequirements
  driver?: RawEmployee
  transit_estimate: string
  tractor_number?: string
  equipment_subtypes: Array<EquipmentSubtypes>
  tarp_type: string | null
  tarp_size: string | null
  power_only: boolean
  tracking_link: string | null
  pro_number?: string
  container?: RawContainer
  pod_required_for_audit: boolean
  trailer_number?: string
  seal_number?: string
  payment_term?: RawPaymentTerm
  third_party_billing_information?: {
    company_name: string
    address: string
    zipcode: string
    city: string
    state: string
    country: string
  } | null
}

export type RawFile = {
  file_url: string | null
}

export type RawPOD = {
  created_at: string
  url: string
  name: string
}

export type RawAttachmentPOD = {
  id: number
  file: string
  file_url: string
  shipment_uuid: string
  type: string
  stop_index: number | null
  document_source: string | null
  created_at: string
}

export type RawShipmentsMetadata = {
  overview: {
    total: number
    pending_appointments: {
      total: number
    }
    missing_pods: {
      total: number
    }
    missing_invoices: {
      total: number
    }
  }
}

export function isRawAttachmentPOD(data: unknown): data is RawAttachmentPOD {
  return (
    typeof data === 'object' &&
    data !== null &&
    'file_url' in data &&
    typeof (data as Record<string, unknown>).file_url === 'string'
  )
}

export function isRawPOD(data: unknown): data is RawPOD {
  if (typeof data !== 'object' || data === null) return false

  const obj = data as Record<string, unknown>
  return (
    typeof obj.url === 'string' &&
    typeof obj.name === 'string' &&
    typeof obj.created_at === 'string' &&
    !('file_url' in obj) // Explicitly not attachment format
  )
}

export type RawManualLocation = {
  city: string
  state: string
  latitude: string
  longitude: string
  date: string
  time: string
}

export type RawInvoiceStatus = DocumentStatus

export type RawInvoice = {
  uuid: string
  file_url: string
  total: number
  file_name: string
  created_at: string
  status: RawInvoiceStatus
}

export type RawCertificate = {
  url: string
  name: string
  created_at: string
}

export type RawOpenDockCarrierId = {
  id: string
}

export type RawOpenDockLoadTypes = {
  id: string
  name: string
  operation: string
  allow_carrier_scheduling: boolean
  allow_carrier_dock_selection: boolean
  direction: string
  docks: Array<{
    id: string
    name: string
    sort_order: number
  }>
  duration_min: number
  equipment_type: string
  mode: string
}

export type RawOpenDockWarehouseDetails = {
  uuid: string
  name: string
  ppe_requirements: Array<string>
  yard_map_img: string
  allow_carrier_scheduling: boolean
  amenities: Array<string>
  instructions: string
  loadtypes: Array<RawOpenDockLoadTypes>
}

export type RawOpenDockWarehouseAvailability = {
  start_times: Array<string>
}

export type RawDockAvailability = {
  dock_id: string
  sort_order: number
  start_times: Array<string>
}

export type OpenDockWarehouseFormFieldType =
  | 'str'
  | 'bigstr'
  | 'timestamp'
  | 'int'
  | 'bool'
  | 'doc'
  | 'multidoc'
  | 'dropdown'
  | 'dropdownmultiselect'
  | 'phone'
  | 'email'
  | 'combobox'
  | 'date'

export type RawOpenDockWarehouseFormField = {
  dropdown_options: Array<string> | null
  label: string
  required: boolean
  type: OpenDockWarehouseFormFieldType
  value: string | string[] | number | boolean | null
}

type FilesUploaded = {
  name: string
  storageUrl: string
  storageKey: string
}[]

type SelectOption = {
  type: string
  value: string
}

export type OpenDockWarehouseFormFieldValue =
  | string
  | number
  | boolean
  | FilesUploaded
  | null
  | SelectOption
  | SelectOption[]

export type OpenDockWarehouseFormField = {
  dropdownOptions: Array<string> | null
  label: string
  required: boolean
  type: OpenDockWarehouseFormFieldType
  value: OpenDockWarehouseFormFieldValue
}

export type RawOpenDockWarehouseCustomFormField = {
  dropdown_options: Array<string> | null
  label: string
  required: boolean
  type: OpenDockWarehouseFormFieldType
  value: string | string[] | number | boolean | null
  id: string
  trigger_id: string
  placeholder: string | null
}

export type OpenDockWarehouseCustomFormField = {
  dropdownOptions: Array<string> | null
  label: string
  required: boolean
  type: OpenDockWarehouseFormFieldType
  value: OpenDockWarehouseFormFieldValue
  id: string
  triggerId: string
  placeholder: string | null
}

export type RawOpenDockWarehouseFormRefNumbers = {
  input_mode: 'alphanumeric' | 'numeric'
  label: string
  required: boolean
  value: string | string[] | null
  help_text: string | null
}

export type OpenDockWarehouseFormRefNumbers = {
  inputMode: 'alphanumeric' | 'numeric'
  label: string
  required: boolean
  value: string | null | string[] | undefined
  helpText: string | null
}

export type RawOpenDockWarehouseFormFields = {
  form_fields: Array<RawOpenDockWarehouseFormField>
  custom_form_fields: Array<RawOpenDockWarehouseCustomFormField>
  ref_numbers?: RawOpenDockWarehouseFormRefNumbers
}

export type OpenDockWarehouseFormFields = {
  formFields: Array<OpenDockWarehouseFormField>
  customFormFields: Array<OpenDockWarehouseCustomFormField>
  refNumbers?: OpenDockWarehouseFormRefNumbers
}

export function mapApiAttachment(attachment: RawAttachment) {
  return {
    id: attachment.id,
    fileName: attachment.file_name,
  }
}

function mapApiToSpotQuote(shipment: RawShipment) {
  const { spot_quote } = shipment
  if (!spot_quote) {
    return undefined
  }

  return {
    uuid: spot_quote.uuid,
    bookedRate: spot_quote.booked_rate
      ? {
          carrier: spot_quote.booked_rate.carrier ?? '',
          flatRate: spot_quote.booked_rate.flat_rate ?? '',
          ltlCarrier: spot_quote.booked_rate.ltl_carrier,
          uuid: spot_quote.booked_rate.uuid,
        }
      : undefined,
    attachments: spot_quote.attachments.map(mapApiAttachment),
    notes:
      spot_quote.notes && spot_quote.notes.trim().length > 0 ? spot_quote.notes.trim() : undefined,
  }
}

function mapApiToCarrier(carrier: RawCarrier): Carrier {
  return {
    dot: carrier.dot || undefined,
    entityType: carrier.entity || undefined,
    id: carrier.id,
    mc: carrier.mc || undefined,
    name: carrier.name,
    uuid: carrier.uuid,
    email: carrier.email,
    scac: carrier.scac ?? undefined,
  }
}

function mapApiToContact(stop: RawStop): Contact | undefined {
  const contact = stop.contact

  if (!contact) {
    return undefined
  }

  return {
    uuid: contact.uuid,
    email: contact.email || '',
    name: contact.name,
    phoneNumberExtension: contact.phone_number_extension || '',
    phoneNumber: contact.phone_number,
  }
}

function mapApiToFacility(facility: RawFacility) {
  if (!facility) return

  return {
    address: facility?.address,
    city: facility.city,
    contacts: facility.contacts.map(contact => ({
      uuid: contact.uuid,
      email: contact.email || '',
      name: contact?.name,
      phoneNumberExtension: contact.phone_number_extension || '',
      phoneNumber: contact.phone_number,
    })),
    country: facility.country,
    state: facility.state,
    uuid: facility.uuid,
    zipcode: facility.zipcode,
    companyName: facility.company_name,
    defaultContactUuid: facility.default_contact_uuid,
    hoursOfOperations: facility.hours_of_operations,
    warehouseUuid: facility.warehouse_uuid,
    warehouseId: facility.warehouse_id,
    warehouseName: facility.warehouse_name,
    timezone: facility.timezone,
    type: facility.type,
  }
}

function mapApiToStops(stops: Array<RawStop>): Array<Stop> {
  return stops.map(stop => ({
    address: stop?.address ? stop.address : undefined,
    checkinTimeliness: stop.checkin_timeliness,
    checkoutTimeliness: stop.checkout_timeliness,
    city: stop?.city,
    country: stop.country,
    date: stop.date,
    defaultContactUuid: stop.default_contact_uuid,
    facility: stop.facility ? mapApiToFacility(stop.facility) : undefined,
    id: stop.id,
    isCheckinLate: stop.is_checkin_late,
    isCheckoutLate: stop.is_checkout_late,
    state: stop.state,
    stopIndex: stop.stop_index,
    stopType: stop.stop_type,
    timezone: stop.timezone,
    zipcode: stop.zipcode,
    appointmentMode: stop.facility?.hours_of_operations?.mode,
    blindBol: stop.blind_bol,
    checkInAt: stop.check_in?.done_at,
    checkOutAt: stop.check_out?.done_at,
    contact: mapApiToContact(stop),
    lat: stop.lat,
    lng: stop.lng,
    notes: stop?.notes ? stop.notes : undefined,
    scaleTicket: stop.scale_ticket,
    appointment: mapApiToAppointment(stop.appointment),
  }))
}

function mapApiToShipper(shipment: RawShipment) {
  if (shipment?.shipper) {
    return {
      companyName: shipment.shipper.company_name,
      logoEmailUrl: shipment.shipper.logo_email_url,
      email: shipment.shipper.email,
      business: shipment.shipper.business,
    }
  }
}

function mapApiToDriver(shipment: RawShipment): Employee | undefined {
  const { driver } = shipment
  if (!driver) {
    return undefined
  }

  return {
    carrier: driver.carrier,
    email: driver.email,
    name: driver.name,
    phoneNumber: driver.phone_number,
    roles: driver.roles,
    uuid: driver.uuid,
  }
}

function mapApiToEffectiveCarrier(shipment: RawShipment): EffectiveCarrier | undefined {
  const { effective_carrier } = shipment

  if (!effective_carrier) return undefined

  return {
    dot: effective_carrier.dot || undefined,
    mc: effective_carrier.mc || undefined,
    name: effective_carrier.name,
  }
}

export function mapApiToAccessorials(rawAccessorials: Array<RawAccessorial>) {
  return rawAccessorials.map(accessorial => ({
    code: accessorial.code,
    name: accessorial.name,
    stopType: accessorial.stop_type,
  }))
}

export function mapApiToThirdPartyBillingInformation(shipment: RawShipment) {
  if (!shipment.third_party_billing_information) {
    return undefined
  }

  return {
    companyName: shipment.third_party_billing_information.company_name,
    address: shipment.third_party_billing_information.address,
    zipcode: shipment.third_party_billing_information.zipcode,
    city: shipment.third_party_billing_information.city,
    state: shipment.third_party_billing_information.state,
    country: shipment.third_party_billing_information.country,
  }
}

export function mapApiToShipment(shipment: RawShipment): Shipment {
  const carrier = mapApiToCarrier(shipment.carrier)
  const driver = mapApiToDriver(shipment)
  const effectiveCarrier = mapApiToEffectiveCarrier(shipment)
  const items = mapApiToShippingItems(shipment.items)
  const shipper = mapApiToShipper(shipment)
  const stops = mapApiToStops(shipment.stops)
  const spotQuote = mapApiToSpotQuote(shipment)
  const managedBy = mapApiToManagedBy(shipment.managed_by)
  const equipmentRequirements = mapApiToEquipmentRequirements(shipment.equipment_requirements)
  const thirdPartyBillingInformation = mapApiToThirdPartyBillingInformation(shipment)

  return {
    uuid: shipment.uuid,
    accessorials: mapApiToAccessorials(shipment.accessorials),
    blindBol: shipment.blind_bol,
    bolNumber: shipment.bol_number,
    equipmentLength: shipment.equipment_length,
    equipmentType: shipment.equipment_type,
    equipmentSubType: shipment?.equipment_subtypes[0],
    equipmentRequirements,
    hazmat: shipment.hazmat,
    isLoadsmartCarrier: shipment.is_loadsmart_carrier,
    managedBy,
    mileage: shipment.mileage,
    mode: shipment.mode,
    poNumbers: shipment.po_numbers,
    refNumber: shipment.ref_number,
    shipperGuideId: shipment.shipper_guide_id,
    shipperRefNumber: shipment.shipper_ref_number,
    soNumbers: shipment.so_numbers || undefined,
    status: shipment.status,
    flatRate: shipment.flat_rate,
    temperature: shipment.temperature,
    carrier,
    effectiveCarrier,
    transitEstimate: shipment?.transit_estimate,
    shipper,
    items,
    stops,
    spotQuote,
    driver,
    measures: mapApiToShippingMeasures(shipment.measures),
    tractorNumber: shipment?.tractor_number || undefined,
    tarpType: shipment.tarp_type || undefined,
    tarpSize: shipment.tarp_size || undefined,
    powerOnly: shipment.power_only,
    trackingLink: shipment?.tracking_link || undefined,
    proNumber: shipment.pro_number,
    container: mapApiToContainer(shipment.container),
    podRequiredForAudit: shipment?.pod_required_for_audit ?? false,
    trailerNumber: shipment.trailer_number ?? undefined,
    sealNumber: shipment.seal_number ?? undefined,
    paymentTerm: shipment.payment_term,
    thirdPartyBillingInformation,
  }
}

export function mapShipmentStatusToApi(shipmentStatus: {
  shipmentUUID: string
  stopId: string | number
  type: string
  date: string
  time: string
}) {
  const { type, date, time } = shipmentStatus

  const checkDate = parseISO(date)
  const checkTime = time.split(':')

  checkDate.setHours(Number(checkTime[0]), Number(checkTime[1]))

  return {
    type,
    done_at: formatISO(checkDate),
  }
}

export function mapApiToPOD(data: unknown): POD {
  if (isRawAttachmentPOD(data)) {
    const urlPath = data.file_url.split('?')[0]
    const fileName = urlPath.split('/').pop() || 'proof-of-delivery.pdf'
    return {
      url: data.file_url || data.file || '',
      name: decodeURIComponent(fileName),
      createdAt: data.created_at,
      stopIndex: data.stop_index ?? undefined,
    }
  }

  if (isRawPOD(data)) {
    return {
      createdAt: data.created_at,
      url: data.url,
      name: data.name,
    }
  }

  throw new TypeError(
    `Invalid POD data: expected RawPOD or RawAttachmentPOD, received ${
      data === null ? 'null' : typeof data
    }`
  )
}

export function mapDriverLocationToApi(data: RawManualLocation) {
  const { city, state, latitude, longitude, date, time } = data

  const formattedDate = parseISO(date)
  const formattedTime = time.split(':')

  formattedDate.setHours(Number(formattedTime[0]), Number(formattedTime[1]))

  return {
    city,
    state,
    latitude: numberFormatter(latitude),
    longitude: numberFormatter(longitude),
    timestamp: formattedDate,
  }
}

export function mapApiToInvoices(data: Array<RawInvoice>): Array<Invoice> {
  return data.map(({ uuid, file_url, total, file_name, created_at, status }) => ({
    uuid,
    url: file_url,
    total,
    name: file_name,
    createdAt: created_at,
    status: status,
  }))
}

export function mapApiToCertificates(data: Array<RawCertificate>) {
  return data.map(({ url, name, created_at }) => ({
    url,
    name,
    createdAt: created_at,
  }))
}

export function mapApiToOpenDockWarehouseDetails(
  data: RawOpenDockWarehouseDetails
): OpenDockWarehouseDetails {
  return {
    uuid: data.uuid,
    name: data.name,
    ppeRequirements: data.ppe_requirements,
    yardMapImg: `${process.env.REACT_APP_OPENDOCK_FILE_URL}${data.yard_map_img}`,
    allowCarrierScheduling: data.allow_carrier_scheduling,
    amenities: data.amenities,
    instructions: isEmpty(data.instructions) ? '-' : data.instructions,
    loadTypes: data.loadtypes.map(loadType => ({
      id: loadType.id,
      name: loadType.name,
      operation: loadType.operation,
      allowCarrierScheduling: loadType.allow_carrier_scheduling,
      allowCarrierDockSelection: loadType.allow_carrier_dock_selection,
      direction: loadType.direction,
      docks: loadType.docks.map(dock => ({
        id: dock.id,
        name: dock.name,
        sortOrder: dock.sort_order,
      })),
      duration: loadType.duration_min,
      equipmentType: loadType.equipment_type,
      mode: loadType.mode,
    })),
  }
}

export function mapOpenDockWarehouseAvailabilityToApi(params: {
  loadTypeId: string
  appointmentDate: string
}) {
  return {
    load_type_id: params.loadTypeId,
    date: formatDate(new Date(params.appointmentDate), 'yyyy-MM-dd'),
  }
}

export function mapApiToWarehouseAvailability(
  data: RawOpenDockWarehouseAvailability,
  facilityTimezone?: string
) {
  return data.start_times.map(time => formatDate(new Date(time), 'hh:mm aa', facilityTimezone))
}

export function mapApiToDocksAvailability(data: RawDockAvailability[], facilityTimezone?: string) {
  return data.map(dock => ({
    dockId: dock.dock_id,
    sortOrder: dock.sort_order,
    startTimes: mapApiToWarehouseAvailability({ start_times: dock.start_times }, facilityTimezone),
  }))
}

export function mapApiToWarehouseFormFields(
  data: RawOpenDockWarehouseFormFields
): OpenDockWarehouseFormFields {
  let parsedFormFields: {
    formFields: Array<OpenDockWarehouseFormField>
    customFormFields: Array<OpenDockWarehouseCustomFormField>
    refNumbers?: OpenDockWarehouseFormRefNumbers
  } = {
    formFields: [],
    customFormFields: [],
    refNumbers: undefined,
  }

  function shallowCamelCase(source: Record<string, unknown>) {
    return mapKeys(source, (_, key) => camelCase(key))
  }

  function sanitizeIntValue(value: string | string[] | number | boolean | null): number | null {
    if (typeof value === 'number') {
      return value
    }

    if (typeof value === 'string') {
      const digitsOnly = value.replace(/[^0-9]/g, '')
      return digitsOnly === '' ? null : Number(digitsOnly)
    }

    return null
  }

  const formFields = data.form_fields.map(field => {
    const camelCasedField = shallowCamelCase(field) as OpenDockWarehouseFormField

    if (field.type === 'int') {
      camelCasedField.value = sanitizeIntValue(field.value)
    }

    return camelCasedField
  })

  const customFormFields = data.custom_form_fields.map(field => {
    const camelCasedField = shallowCamelCase(field) as OpenDockWarehouseCustomFormField

    if (field.type === 'int') {
      camelCasedField.value = sanitizeIntValue(field.value)
    }

    return camelCasedField
  })

  parsedFormFields.formFields = formFields
  parsedFormFields.customFormFields = customFormFields

  if (data.ref_numbers) {
    parsedFormFields.refNumbers = {
      label: data.ref_numbers.label,
      required: data.ref_numbers.required,
      inputMode: data.ref_numbers.input_mode,
      value: data.ref_numbers.value ?? undefined,
      helpText: data.ref_numbers.help_text,
    }
  }

  return parsedFormFields
}

export function mapAppointmentDateAndTimeToApi(
  date: string,
  time: string,
  facilityTimezone: string
) {
  const newDate = new Date(date)
  const [hours, rest] = time.split(':')
  const [minutes, shift] = rest.split(' ')

  let hourIn24Format = Number(hours)

  if (shift === 'PM' && hourIn24Format !== 12) {
    hourIn24Format += 12
  } else if (shift === 'AM' && hourIn24Format === 12) {
    hourIn24Format = 0
  }

  newDate.setHours(hourIn24Format, Number(minutes), 0, 0)

  return zonedTimeToUtc(newDate, facilityTimezone).toISOString()
}

function mapAppointmentFormFieldsValueByTypeToApi(
  field: OpenDockWarehouseFormField | OpenDockWarehouseCustomFormField,
  facilityTimezone: string
): string | number | boolean | string[] | null {
  switch (field.type) {
    case 'str':
    case 'int':
    case 'phone':
    case 'email':
    case 'bigstr':
      return typeof field.value === 'string' || typeof field.value === 'number' ? field.value : null
    case 'bool':
      return Boolean(field.value)
    case 'dropdown':
    case 'combobox':
      return (field.value as SelectOption)?.value ?? null
    case 'dropdownmultiselect':
      return Array.isArray(field.value)
        ? (field.value as SelectOption[]).map(option => option.value)
        : null
    case 'date':
    case 'timestamp':
      if (typeof field.value === 'string') {
        return zonedTimeToUtc(new Date(field.value), facilityTimezone).toISOString()
      }
      return null
    case 'doc':
      const docFiles = field.value as FilesUploaded

      if (docFiles && docFiles.length > 0) {
        return docFiles[0].storageKey
      }

      return null
    case 'multidoc':
      const multiDocFiles = field.value as FilesUploaded

      if (multiDocFiles && multiDocFiles.length === 0) {
        return null
      }

      return multiDocFiles.map(file => file.storageKey)
    default:
      return null
  }
}

export function mapOpenDockWarehouseAppointmentToApi(params: {
  staticFields: ScheduleFormValues
  warehouseFormFields: OpenDockWarehouseFormFields
  openDockCarrierId: string | undefined
  facilityTimezone: string
}): {
  carrier_id?: string
  load_type_id: string
  dock_id: string
  start: string
  ref_numbers?: { value: string | string[] | undefined }
  form_fields: RawOpenDockWarehouseFormFields['form_fields']
  custom_form_fields: RawOpenDockWarehouseFormFields['custom_form_fields']
} {
  function shallowSnakeCase(source: Record<string, unknown>) {
    return mapKeys(source, (_, key) => snakeCase(key))
  }

  function splitIfIsArray(value: string | string[] | null | undefined) {
    if (Array.isArray(value)) {
      return value
    }
    if (value) {
      return value.split(',').map(item => item.trim())
    }
    return value
  }

  const payload = {
    load_type_id: params.staticFields.loadType!.id,
    dock_id: params.staticFields.dock!.value,
    start: mapAppointmentDateAndTimeToApi(
      String(params.staticFields.appointmentDate),
      String(params.staticFields.appointmentTime),
      params.facilityTimezone
    ),
    ref_numbers: {
      value: splitIfIsArray(params.warehouseFormFields.refNumbers?.value) || undefined,
    },
    form_fields: params.warehouseFormFields.formFields.map(field => {
      return {
        ...(shallowSnakeCase(field) as RawOpenDockWarehouseFormField),
        value: mapAppointmentFormFieldsValueByTypeToApi(field, params.facilityTimezone),
      }
    }),
    custom_form_fields: params.warehouseFormFields.customFormFields.map(field => {
      return {
        ...(shallowSnakeCase(field) as RawOpenDockWarehouseCustomFormField),
        value: mapAppointmentFormFieldsValueByTypeToApi(field, params.facilityTimezone),
      }
    }),
  }

  if (params.openDockCarrierId) {
    return { ...payload, carrier_id: params.openDockCarrierId }
  }

  return payload
}

export function mapApiToShipmentsMetadata(data: RawShipmentsMetadata) {
  return {
    total: data.overview.total,
    pendingAppointments: data.overview.pending_appointments.total,
    missingPods: data.overview.missing_pods.total,
    missingInvoices: data.overview.missing_invoices.total,
  }
}
